import React, { FC } from 'react';
import { FieldArrayRenderProps, useFormikContext } from 'formik';
import { isEmpty, uniqueId } from 'lodash';
import { Button } from '@qbe/ads-core';
import { FormCard, Toggle } from '@qbe/ads-blocks-forms';
import { useContent, useContentContext } from '@qbe/dfc-content';

import FormSpacer from 'components/FormSpacer/FormSpacer';
import { IncomeData, IncomeFormFields } from 'contexts/FormProvider';
import { YesNoList } from 'utils/constants';
import validationSchema from '../schema';

interface Props {
  arrayHelpers: FieldArrayRenderProps;
}

const IncomeStatus: FC<Props> = ({ arrayHelpers }) => {
  const { values, setFieldValue, setFieldTouched, resetForm } = useFormikContext<IncomeFormFields>();

  const { getTextContent } = useContent({
    context: useContentContext(),
  });

  const isDisabled = !validationSchema.isValidSync(values);

  const hasIncome = values.incomeStatus === 'yes';

  // Localized content constants
  const sectionHeading = getTextContent('Income.details.heading');
  const incomeStatusTitle = getTextContent('Income.details.incomeStatus');
  const submitLabel = getTextContent('Income.details.submit.label');

  return (
    <FormCard variant="sectionTitle" title={sectionHeading}>
      <Toggle
        title={incomeStatusTitle}
        required
        name="incomeStatus"
        optionList={YesNoList.map((option) => ({
          id: option.value,
          label: option.text,
          value: option.value,
          checked: values.incomeStatus === option.value,
          'data-track': 'incomeStatus',
          'data-track-value': 'capture',
          onBlur: () => setFieldTouched('incomeStatus', true),
          onChange: (event) => {
            const value = event.currentTarget.value;
            setFieldValue('incomeStatus', value);

            if (isEmpty(values.incomeData) && value === 'yes') {
              const newDetails: IncomeData = {
                id: uniqueId(),
                incomeType: '',
                incomeDescription: '',
                incomeAmount: '',
              };

              arrayHelpers.push(newDetails);
            }

            if (!isEmpty(values.incomeData) && value === 'no') {
              resetForm({
                errors: {
                  incomeData: [],
                  incomeStatus: '',
                },
              });

              setFieldValue('incomeData', []);
              setFieldValue('incomeStatus', value);
            }
          },
        }))}
      />

      {!hasIncome && (
        <>
          <FormSpacer type="formgroup" />
          <Button type="submit" label={submitLabel} disabled={isDisabled} />
        </>
      )}
    </FormCard>
  );
};

export default IncomeStatus;
